import { create } from 'zustand';

export type WorkspaceRole = 'Reader' | 'Editor' | 'Reviewer' | 'Admin';

export interface WorkspaceMember {
  userId: string;
  displayName: string;
  email: string | null;
  role: WorkspaceRole;
  addedBy: string | null;
  createdAt: string;
  isOrgAdmin: boolean;
}

interface WorkspaceMemberState {
  members: WorkspaceMember[];
  loading: boolean;
  error: string | null;
  loadMembers: (workspaceId: string, token?: string | null) => Promise<void>;
  addMember: (workspaceId: string, userId: string, role: WorkspaceRole, token?: string | null) => Promise<void>;
  updateRole: (workspaceId: string, userId: string, role: WorkspaceRole, token?: string | null) => Promise<void>;
  removeMember: (workspaceId: string, userId: string, token?: string | null) => Promise<void>;
}

const base = (workspaceId: string) =>
  `/api/dotnet/workspaces/${workspaceId}/members`;

export const useWorkspaceMemberStore = create<WorkspaceMemberState>()((set, get) => ({
  members: [],
  loading: false,
  error: null,

  loadMembers: async (workspaceId, token) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(base(workspaceId), { headers });
      if (!response.ok) throw new Error('Failed to load workspace members.');

      const members = (await response.json()) as WorkspaceMember[];
      set({ members, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load workspace members.',
      });
    }
  },

  addMember: async (workspaceId, userId, role, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(base(workspaceId), {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, role }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body ?? 'Mitglied konnte nicht hinzugefügt werden.');
    }

    const member = (await response.json()) as WorkspaceMember;
    const existing = get().members;
    // Idempotent: replace if already in list (server returns updated member on 200)
    const updated = existing.some((m) => m.userId === member.userId)
      ? existing.map((m) => (m.userId === member.userId ? member : m))
      : [...existing, member];
    set({ members: updated, error: null });
  },

  updateRole: async (workspaceId, userId, role, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${base(workspaceId)}/${userId}/role`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body ?? 'Rolle konnte nicht geändert werden.');
    }

    set({
      members: get().members.map((m) => (m.userId === userId ? { ...m, role } : m)),
      error: null,
    });
  },

  removeMember: async (workspaceId, userId, token) => {
    const headers: HeadersInit = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${base(workspaceId)}/${userId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body ?? 'Mitglied konnte nicht entfernt werden.');
    }

    set({
      members: get().members.filter((m) => m.userId !== userId),
      error: null,
    });
  },
}));
